import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { Plus, Pencil, Trash2, Loader2, Server, Globe, Smartphone } from 'lucide-react';
import { toast } from 'sonner';

// Map of kind → display config. Each kind renders one card with its own
// CRUD UI; the same component is used 3x on the SettingsPage.
const KIND_META = {
  hosting_provider: { title: 'Hosting Providers', icon: Server, placeholder: 'e.g. AWS, DigitalOcean' },
  domain_registrar: { title: 'Domain Registrars', icon: Globe, placeholder: 'e.g. GoDaddy, Namecheap' },
  telecom_operator: { title: 'Telecom Operators (DLT)', icon: Smartphone, placeholder: 'e.g. Jio, Airtel' },
};

export const LookupsAdmin = ({ kind }) => {
  const meta = KIND_META[kind];
  const Icon = meta?.icon || Server;
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', is_active: true });
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchRows = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/lookups/${kind}?include_inactive=true`);
      if (r.data?.success) setRows(r.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load');
    } finally { setLoading(false); }
  }, [kind]);

  useEffect(() => { fetchRows(); }, [fetchRows]);

  const openCreate = () => { setEditing(null); setForm({ name: '', is_active: true }); setOpenDialog(true); };
  const openEdit = (d) => { setEditing(d); setForm({ name: d.name || '', is_active: d.is_active !== false }); setOpenDialog(true); };

  const save = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/lookups/${kind}/${editing.id}`, form);
        toast.success('Updated');
      } else {
        await api.post(`/lookups/${kind}`, form);
        toast.success('Added');
      }
      setOpenDialog(false);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.delete(`/lookups/${kind}/${deleteTarget.id}`);
      toast.success('Removed');
      setDeleteTarget(null);
      fetchRows();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setDeleting(false); }
  };

  if (!meta) return null;

  return (
    <Card data-testid={`lookups-${kind}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Icon className="h-5 w-5 text-primary" />
            {meta.title}
          </CardTitle>
          <Button size="sm" onClick={openCreate} data-testid={`lookups-${kind}-add`}>
            <Plus className="h-4 w-4 mr-1.5" />Add
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
        ) : rows.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No entries yet.</p>
        ) : (
          <div className="divide-y divide-border">
            {rows.map(r => (
              <div key={r.id} className="flex items-center justify-between py-2.5" data-testid={`lookups-${kind}-row-${r.slug}`}>
                <div className="flex items-center gap-3">
                  <span className={`text-sm font-medium ${r.is_active === false ? 'text-muted-foreground line-through' : ''}`}>{r.name}</span>
                  {r.is_system && <span className="text-[10px] uppercase tracking-wide bg-muted text-muted-foreground px-1.5 py-0.5 rounded">system</span>}
                  {r.is_active === false && <span className="text-[10px] uppercase tracking-wide bg-muted text-muted-foreground px-1.5 py-0.5 rounded">inactive</span>}
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(r)} data-testid={`lookups-${kind}-edit-${r.slug}`}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setDeleteTarget(r)} data-testid={`lookups-${kind}-delete-${r.slug}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit' : 'Add'} {meta.title.replace(/s$/, '')}</DialogTitle>
            <DialogDescription>Will be available as a dropdown option on the relevant page.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-sm">Name</Label>
              <Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder={meta.placeholder} autoFocus data-testid={`lookups-${kind}-name-input`} />
            </div>
            <div className="flex items-center justify-between">
              <Label className="text-sm">Active</Label>
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm(f => ({ ...f, is_active: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)} disabled={saving}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid={`lookups-${kind}-save`}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? 'Update' : 'Add')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove &quot;{deleteTarget?.name}&quot;?</AlertDialogTitle>
            <AlertDialogDescription>Records that currently reference this value will keep it as free text. New entries will no longer see it in the dropdown.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={deleting} className="bg-destructive hover:bg-destructive/90">
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Remove'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};
